import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import TicketPeekDetail from './TicketPeekDetail';
import { downloadCsv, downloadXlsx } from '../lib/exportTable';
import { hours, hoursDelta } from '../lib/hours';
import { money, rate } from '../lib/money';
import './BillingInvoices.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const hrs = hours;
const day = (v) => (v ? String(v).slice(0, 10) : '—');

/**
 * The columns of the detail table, declared once.
 *
 * The lines on screen and the two downloads are generated from this list, so a
 * column added here appears in all three and they cannot drift - which is the
 * only reason anybody trusts an exported figure enough to send it to an
 * accountant.
 */
const TICKET_COLUMNS = [
  { key: 'invoice_number',  label: 'Invoice',      width: 16 },
  { key: 'contractor_name', label: 'Contractor',   width: 20 },
  { key: 'ticket_number',   label: 'Ticket',       width: 14 },
  { key: 'ticket_title',    label: 'Job',          width: 40 },
  { key: 'category',        label: 'Category',     width: 16 },
  { key: 'requester_name',  label: 'Raised by',    width: 18 },
  { key: 'approver_name',   label: 'Approved by',  width: 18 },
  { key: 'approved_at',     label: 'Approved',     width: 12, type: 'date' },
  { key: 'billed_at',       label: 'Billed',       width: 12, type: 'date' },
  { key: 'hours_worked',    label: 'Hours worked', width: 13, type: 'number' },
  { key: 'hours_billed',    label: 'Hours billed', width: 13, type: 'number' },
  { key: 'hours_variance',  label: 'Variance',     width: 11, type: 'number' },
  { key: 'hourly_rate',     label: 'Rate',         width: 10, type: 'money' },
  { key: 'labour',          label: 'Labour',       width: 12, type: 'money' },
  { key: 'expenses',        label: 'Expenses',     width: 12, type: 'money' },
  { key: 'amount',          label: 'Amount',       width: 12, type: 'money' },
  { key: 'status',          label: 'Status',       width: 12 }
];

/** A figure that may legitimately be unknown reads as a dash, never as zero. */
const maybeHours = (v) => (v === null || v === undefined ? '—' : hrs(v));

/**
 * What the contractors have invoiced, as NCMB reads it.
 *
 * The contractor portal has had a view of this for a while and it is the
 * contractor's own - their work, their invoices. From the office there was
 * nothing: answering "what was on invoice 41" meant knowing the number first
 * and looking it up one at a time, and "what has Jane billed us this quarter"
 * could not be asked at all.
 *
 * Grouped by invoice, because that is the unit the person paying it works in.
 * The tickets are underneath, one press away, with their own hours and amount,
 * and a ticket opens in place rather than navigating off the page - somebody
 * checking an invoice is working down a list, and losing their place in it to
 * read one job is how checking an invoice becomes an afternoon.
 *
 * Hours worked and hours billed are both shown, everywhere, on purpose. Worked
 * is the sum of the sessions actually recorded against the ticket; billed is
 * what was typed into the billing form when the invoice was raised. They
 * usually agree, and the cases where they do not are the entire reason this
 * page exists.
 */
export default function BillingInvoices() {
  const [data, setData] = useState({ invoices: [], totals: null, filters: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [peek, setPeek] = useState(null);

  const [contractor, setContractor] = useState('all');
  const [approver, setApprover] = useState('all');
  const [requester, setRequester] = useState('all');
  const [status, setStatus] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');

  const params = useMemo(() => {
    const p = {};
    if (contractor !== 'all') p.contractor = contractor;
    if (approver !== 'all') p.approver = approver;
    if (requester !== 'all') p.requester = requester;
    if (status !== 'all') p.status = status;
    if (from) p.from = from;
    if (to) p.to = to;
    if (search.trim()) p.q = search.trim();
    return p;
  }, [contractor, approver, requester, status, from, to, search]);

  const filtered = Object.keys(params).length > 0;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE}/billing/invoices`, { params });
      setData({
        invoices: Array.isArray(r.data?.invoices) ? r.data.invoices : [],
        totals: r.data?.totals || null,
        // The option lists come from all invoiced work rather than from the
        // filtered rows, so they are kept across a filter change - otherwise
        // choosing an approver empties the list that chose them.
        filters: r.data?.filters || null
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the invoices');
      setData((d) => ({ ...d, invoices: [], totals: null }));
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Typing in the search box should not be one request per keystroke. `search`
  // is in the deps because the effect reads it; it changes `load` anyway, so
  // naming it costs nothing and keeps the lint rule honest rather than
  // switched off.
  useEffect(() => {
    const id = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [load, search]);

  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const { invoices, totals, filters } = data;

  // One flat row per ticket, carrying its invoice down with it, so a download
  // is the whole picture rather than a list of totals with no lines.
  const flatRows = useMemo(() => invoices.flatMap((inv) =>
    inv.tickets.map((t) => ({
      ...t,
      invoice_number: inv.invoice_number,
      contractor_name: inv.contractor_name
    }))), [invoices]);

  const base = `invoices-${new Date().toISOString().slice(0, 10)}`;
  const clear = () => {
    setContractor('all'); setApprover('all'); setRequester('all');
    setStatus('all'); setFrom(''); setTo(''); setSearch('');
  };

  const opt = (list) => (Array.isArray(list) ? list : []);

  return (
    <div className="bi">
      <div className="bi-controls">
        <label className="bi-filter bi-search">
          <span className="bi-sr">Search</span>
          {/* One box across the invoice, the ticket, the job and the people on
              it: somebody hunting for a line remembers a name or half an
              invoice number, not which column it lives in. */}
          <input
            type="search"
            value={search}
            placeholder="Invoice, ticket, job, contractor or person"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <label className="bi-filter">
          <span className="bi-sr">Contractor</span>
          <select value={contractor} onChange={(e) => setContractor(e.target.value)}
                  aria-label="Filter by contractor">
            <option value="all">All contractors</option>
            {opt(filters?.contractors).map((c) => (
              <option key={c.id} value={c.id}>
                {c.company ? `${c.name} — ${c.company}` : c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="bi-filter">
          <span className="bi-sr">Approver</span>
          <select value={approver} onChange={(e) => setApprover(e.target.value)}
                  aria-label="Filter by approver">
            <option value="all">All approvers</option>
            {opt(filters?.approvers).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </label>

        <label className="bi-filter">
          <span className="bi-sr">Raised by</span>
          <select value={requester} onChange={(e) => setRequester(e.target.value)}
                  aria-label="Filter by who raised the ticket">
            <option value="all">Raised by anyone</option>
            {opt(filters?.requesters).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </label>

        {opt(filters?.statuses).length > 1 && (
          <label className="bi-filter">
            <span className="bi-sr">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    aria-label="Filter by billing status">
              <option value="all">Any status</option>
              {opt(filters?.statuses).map((s) => (
                <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </label>
        )}

        {/* Dated on when the work was billed, which is what an invoice is
            dated by. Both ends are inclusive. */}
        <label className="bi-filter bi-date">
          <span className="bi-date-label">Billed from</span>
          <input type="date" value={from} max={to || undefined}
                 onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="bi-filter bi-date">
          <span className="bi-date-label">to</span>
          <input type="date" value={to} min={from || undefined}
                 onChange={(e) => setTo(e.target.value)} />
        </label>

        {filtered && (
          <button type="button" className="bi-btn" onClick={clear}>Clear</button>
        )}
        {flatRows.length > 0 && (
          <>
            <button type="button" className="bi-btn"
                    onClick={() => downloadCsv(TICKET_COLUMNS, flatRows, base)}>
              Download CSV
            </button>
            <button type="button" className="bi-btn"
                    onClick={() => downloadXlsx(TICKET_COLUMNS, flatRows, base, 'Invoices')}>
              Download Excel
            </button>
          </>
        )}
      </div>

      {error && <p className="bi-error" role="alert">{error}</p>}

      {totals && invoices.length > 0 && (
        <div className="bi-figures" role="status">
          <span><strong>{totals.invoices}</strong> invoice{totals.invoices === 1 ? '' : 's'}</span>
          <span><strong>{totals.tickets}</strong> ticket{totals.tickets === 1 ? '' : 's'}</span>
          <span><strong>{maybeHours(totals.hours_worked)}</strong> worked</span>
          <span><strong>{hrs(totals.hours_billed)}</strong> billed</span>
          {/* Only when there is something to reconcile. An "Expenses €0.00"
              on every invoice is noise; a total that silently includes them
              is worse. */}
          {totals.expenses > 0 && (
            <span><strong>{money(totals.expenses)}</strong> expenses</span>
          )}
          <span className="bi-amount"><strong>{money(totals.amount)}</strong></span>
        </div>
      )}

      {loading && invoices.length === 0 && <p className="bi-quiet">Loading invoices…</p>}

      {!loading && invoices.length === 0 && !error && (
        <p className="bi-quiet">
          {filtered
            ? 'No invoices match those filters.'
            : 'Nothing has been invoiced yet. A job appears here once a contractor '
              + 'gives it an invoice number in their portal, and stays for good — so this '
              + 'is where to look when somebody asks what a particular invoice covered.'}
        </p>
      )}

      {invoices.length > 0 && (
        <div className="bi-table-wrap">
          <table className="bi-table">
            <thead>
              <tr>
                <th className="bi-chevron-col"><span className="bi-sr">Expand</span></th>
                <th>Invoice</th>
                <th>Contractor</th>
                <th>Billed</th>
                <th className="bi-num">Tickets</th>
                <th className="bi-num">Hours worked</th>
                <th className="bi-num">Hours billed</th>
                <th className="bi-num">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const isOpen = open.has(inv.key);
                // Worked against billed, at invoice level. A difference here is
                // the first thing somebody checking an invoice wants to see,
                // and it is the reason both figures are on the summary row
                // rather than only inside.
                const differs = inv.hours_worked !== null
                  && Math.abs(inv.hours_worked - inv.hours_billed) > 0.009;
                return (
                  <React.Fragment key={inv.key}>
                    <tr className={isOpen ? 'bi-invoice is-open' : 'bi-invoice'}>
                      <td className="bi-chevron-col">
                        {/* The whole row is not the control: a row that is both
                            a button and a table of figures cannot be read by a
                            screen reader as either. One named button. */}
                        <button
                          type="button"
                          className="bi-expand"
                          aria-expanded={isOpen}
                          onClick={() => toggle(inv.key)}
                          title={`${isOpen ? 'Hide' : 'Show'} the tickets on ${inv.invoice_number}`}
                        >
                          <span className="bi-sr">
                            {isOpen ? 'Hide' : 'Show'} the tickets on {inv.invoice_number}
                          </span>
                          <svg className={isOpen ? 'bi-chev is-open' : 'bi-chev'}
                               viewBox="0 0 16 16" fill="none" stroke="currentColor"
                               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
                               aria-hidden="true">
                            <path d="M4 6.5 8 10.5 12 6.5" />
                          </svg>
                        </button>
                      </td>
                      <td className="bi-invoice-no">{inv.invoice_number}</td>
                      <td>
                        <span className="bi-contractor">{inv.contractor_name || '—'}</span>
                        {inv.company && <small className="bi-company">{inv.company}</small>}
                      </td>
                      <td className="bi-date-cell">
                        {day(inv.first_billed_at)}
                        {inv.last_billed_at && day(inv.last_billed_at) !== day(inv.first_billed_at)
                          && <small className="bi-company">to {day(inv.last_billed_at)}</small>}
                      </td>
                      <td className="bi-num">{inv.ticket_count}</td>
                      <td className={differs ? 'bi-num bi-differs' : 'bi-num'}>
                        {maybeHours(inv.hours_worked)}
                      </td>
                      <td className="bi-num">{hrs(inv.hours_billed)}</td>
                      <td className="bi-num bi-amount">{money(inv.amount)}</td>
                      <td>
                        <span className="bi-status" data-status={inv.status}>{inv.status}</span>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bi-detail-row">
                        <td colSpan={9}>
                          <table className="bi-detail">
                            <thead>
                              <tr>
                                <th>Ticket</th>
                                <th>Job</th>
                                <th>Raised by</th>
                                <th>Approved by</th>
                                <th className="bi-num">Hours worked</th>
                                <th className="bi-num">Hours billed</th>
                                <th className="bi-num">Variance</th>
                                <th className="bi-num">Rate</th>
                                <th className="bi-num">Labour</th>
                                <th className="bi-num">Expenses</th>
                                <th className="bi-num">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inv.tickets.map((t) => (
                                <tr key={t.id} className="bi-ticket">
                                  <td>
                                    {/* A billing line whose ticket has been
                                        deleted is still money on the invoice,
                                        so it is shown rather than dropped -
                                        there is just nothing to open. */}
                                    {t.ticket_id ? (
                                      <button
                                        type="button"
                                        className="bi-ticket-link"
                                        onClick={() => setPeek({
                                          ...t, invoice_number: inv.invoice_number
                                        })}
                                        title={`Open ${t.ticket_number}`}
                                      >
                                        {t.ticket_number}
                                      </button>
                                    ) : (
                                      <span className="bi-gone" title="This ticket no longer exists">
                                        no ticket
                                      </span>
                                    )}
                                  </td>
                                  <td className="bi-job">{t.ticket_title || '—'}</td>
                                  <td>{t.requester_name || '—'}</td>
                                  <td>{t.approver_name || <span className="bi-gone">none recorded</span>}</td>
                                  <td className="bi-num">{maybeHours(t.hours_worked)}</td>
                                  <td className="bi-num">{hrs(t.hours_billed)}</td>
                                  <td className={t.hours_variance ? 'bi-num bi-differs' : 'bi-num'}>
                                    {t.hours_variance === null ? '—' : hoursDelta(t.hours_variance)}
                                  </td>
                                  <td className="bi-num">{rate(t.hourly_rate)}</td>
                                  {/* The hours alone, then what was reimbursed
                                      on top, so hours x rate + expenses adds
                                      up to the amount on screen. Without this
                                      the row read "4h at EUR 50" beside EUR
                                      252.30 and nothing accounted for the
                                      difference. */}
                                  <td className="bi-num">{money(t.labour)}</td>
                                  <td className={t.expenses > 0 ? 'bi-num bi-differs' : 'bi-num'}>
                                    {t.expenses > 0 ? money(t.expenses) : '—'}
                                  </td>
                                  <td className="bi-num bi-amount">{money(t.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {peek && (
        <TicketPeekDetail
          ticketId={peek.ticket_id}
          ticketNumber={peek.ticket_number}
          title={peek.ticket_title}
          onClose={() => setPeek(null)}
          /* What this list knows that the ticket itself does not: the money.
             TicketPeekDetail takes these as {label, value} and renders them
             alongside its own facts, so a ticket looks the same whichever list
             it was opened from. */
          extra={[
            { label: 'Invoice', value: peek.invoice_number },
            { label: 'Hours worked', value: maybeHours(peek.hours_worked) },
            { label: 'Hours billed', value: hrs(peek.hours_billed) },
            { label: 'Rate', value: rate(peek.hourly_rate) },
            { label: 'Labour', value: money(peek.labour) },
            ...(peek.expenses > 0
              ? [{ label: 'Expenses reimbursed', value: money(peek.expenses) }] : []),
            { label: 'Amount', value: money(peek.amount) },
            { label: 'Approved by', value: peek.approver_name || 'none recorded' }
          ]}
        />
      )}
    </div>
  );
}
